import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ReviewPanel } from "@/components/shared/ReviewPanel";
import { Avatar } from "@/components/shared/Avatar";
import { TimeStepper } from "@/components/shared/TimeStepper";
import { Step } from "@/components/shared/ShiftParts";
import { BookOpen, MoreVertical, Trash2, UserPlus } from "lucide-react";
import { KINDS, autoBrk, fmtHours, hh, hm, hoursOf, num, rowSpan, toMin } from "@/components/diary/CrewTimeline";

// 423: panely party v deníku ve stejném vzhledu jako okno směny (422) — bílý panel zprava
// (na telefonu zespodu), velké číslo hodin, osa Příchod → Přestávka → Odchod s tužkami,
// čas −/+ po 15 min i číslicemi. Ukládání se nemění: Uložit = hned PUT party (schválená směna z deníku).
const DAYS = ["ne", "po", "út", "st", "čt", "pá", "so"];
const dayLabel = (iso) => { if (!iso) return ""; const d = new Date(`${iso}T12:00:00`); return `${DAYS[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}.`; };
const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} aria-pressed={on} data-testid={testid}
    className={`h-9 px-3 rounded-full border text-[13px] font-semibold tabular-nums ${on ? "border-brand bg-brand/10 text-brand" : "border-border bg-card hover:border-muted-foreground"}`}>{children}</button>
);
const CARD = "rounded-[20px] bg-background dark:bg-secondary px-3.5 py-1";

function PanelMenu({ label, onRemove, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative shrink-0">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-label="Další akce" aria-expanded={open} data-testid={`${testid}-menu`}
        className="h-9 w-9 grid place-items-center rounded-[10px] border border-border text-muted-foreground hover:text-foreground"><MoreVertical size={17} /></button>
      {open && (
        <div className="absolute right-0 mt-1 w-52 rounded-[12px] border border-border bg-popover shadow-lg z-20 p-1" onMouseLeave={() => setOpen(false)}>
          <button type="button" onClick={() => { setOpen(false); onRemove(); }} data-testid={`${testid}-remove`}
            className="w-full text-left px-3 py-2 rounded-[8px] text-sm font-semibold text-destructive hover:bg-destructive/10 flex items-center gap-2"><Trash2 size={15} /> {label}</button>
        </div>
      )}
    </div>
  );
}

// Patička jen při změně (u „Všem stejně“ a lidí mimo tým vždy) — bez důvodu, deník je zdroj
const Foot = ({ children }) => (
  <div className="border-t border-border bg-card px-4 sm:px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex flex-wrap items-center justify-end gap-2">{children}</div>
);

// Člověk v partě (nebo „Všem v partě stejně“, když row == null a all == true)
export function CrewPanel({ open, row, all, count, rule, others = [], date, position, onClose, onSave, onRemove }) {
  const [f, setF] = useState(null);
  const [edit, setEdit] = useState(null);
  const init = useRef(null);
  useEffect(() => {
    if (!open) return;
    const sp = row ? rowSpan(row, rule) : null;
    const v = { a: sp?.a ?? 7 * 60, b: sp?.b ?? 15 * 60 + 30, auto: row ? row.brk == null : true, brk: row?.brk ?? rule?.minutes ?? 30,
      at: sp?.at ?? toMin(rule?.start) ?? 720, err: "" };
    init.current = v; setF(v); setEdit(null);
  }, [open, JSON.stringify(row || {}), rule]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return null;
  const autoLen = (a, b) => autoBrk(rule, (b - a - (rule?.minutes ?? 30)) / 60);
  const len = f.auto ? autoLen(f.a, f.b) : f.brk;
  const hours = Math.round(hoursOf(f.a, f.b, f.auto ? null : f.brk, rule) * 4) / 4;
  const at = len ? Math.min(Math.max(f.at, f.a), f.b - len) : null;
  const otherH = others.reduce((s, x) => s + (x.hours || 0), 0);
  const i0 = init.current || f;
  const brkChanged = f.auto !== i0.auto || (!f.auto && f.brk !== i0.brk) || (len > 0 && f.at !== i0.at);
  const dirty = !all && (f.a !== i0.a || f.b !== i0.b || brkChanged);
  const set = (p) => setF((x) => ({ ...x, ...p, err: "" }));
  const save = () => {
    if (f.b - f.a < 30) return setF({ ...f, err: "Konec musí být aspoň 30 minut po začátku." });
    const clash = others.find((o) => o.a != null && f.a < o.b && f.b > o.a);
    if (clash && !all) return setF({ ...f, err: `Překrývá se s ${clash.project} ${hm(clash.a)}–${hm(clash.b)}.` });
    onSave({ start: hh(f.a), hours, brk: f.auto ? null : f.brk, brkAt: len && !f.auto ? hh(at) : (row?.brkAt && len ? hh(at) : null) });
  };
  const discard = () => { setF({ ...init.current }); setEdit(null); };
  const toggle = (k) => (all ? null : () => setEdit((e) => (e === k ? null : k)));
  const isOpen = (k) => all || edit === k;
  const tv = (k) => <span className={f[k] !== i0[k] && !all ? "text-brand" : ""}>{hm(f[k])}</span>;
  const name = row?.name || "";

  return (
    <ReviewPanel open={open} onClose={onClose} dirty={dirty} testid="crew-panel" glass={false} noun="člověk"
      title={all ? `Všem v partě stejně · ${count}` : `${name} · parta`} mobileClass="max-w-xl [&>*]:min-w-0">
      {(controls) => (<>
        <div className="pl-4 sm:pl-6 pr-14 md:pr-5 pt-1 md:pt-4 pb-2 flex items-center gap-2">
          <div className="flex-1 min-w-0">
            {all ? <b className="text-[15px] font-bold" data-testid="crew-panel-title">Všem v partě stejně · {count}</b>
              : <span className="inline-flex items-center gap-1.5 text-[12px] font-bold rounded-full px-2.5 py-1 text-success bg-success/10" data-testid="crew-panel-status"><BookOpen size={13} /> Zapsáno v deníku</span>}
          </div>
          {!all && <PanelMenu label="Odstranit z deníku" onRemove={onRemove} testid="crew-panel" />}
          {controls}
        </div>

        <div className="overflow-y-auto overscroll-contain px-4 sm:px-6 pb-6 space-y-3.5 min-w-0">
          <div className="grid justify-items-center text-center gap-1 pt-1">
            {!all && (
              <span className="relative">
                <Avatar name={name} size={64} className="bg-brand/10 text-brand" />
                <span className="absolute -right-1 -bottom-1 h-6 w-6 rounded-full grid place-items-center text-white border-[3px] border-card bg-success"><BookOpen size={11} /></span>
              </span>
            )}
            {!all && (
              <p className="mt-1.5 text-[15px] font-bold max-w-full truncate">{name}{position ? <span className="text-muted-foreground font-semibold"> · {position}</span> : null}</p>
            )}
            <p className="text-[13px] text-muted-foreground">parta v deníku{date ? ` · ${dayLabel(date)}` : ""}</p>
            <p className={`text-[44px] sm:text-[52px] leading-none font-black tracking-tight tabular-nums mt-1 ${dirty ? "text-brand" : ""}`} data-testid="crew-panel-hours">
              {fmtHours(hours)}<span className="text-[15px] font-bold text-muted-foreground tracking-normal">{all ? " každému" : " tady"}</span>
            </p>
            <p className="text-[13.5px] text-muted-foreground tabular-nums">{hm(f.a)}–{hm(f.b)} · {len ? `přestávka ${len} min` : "bez přestávky"}</p>
            {!all && otherH > 0 && (
              <p className="text-[12.5px] text-muted-foreground" data-testid="crew-panel-day">
                celkem za den <b className="text-foreground">{fmtHours(hours + otherH)}</b> · i {others.map((o) => `${o.project} ${hm(o.a)}–${hm(o.b)}`).join(", ")}
              </p>
            )}
          </div>

          <div className={CARD} data-testid="crew-panel-time">
            <Step prefix="crew-panel" dot="ok" label="Příchod" sub={all ? "stejně všem v partě" : "zapsáno v deníku"} value={tv("a")} editKey="in" onEdit={toggle("in")} open={isOpen("in")}>
              <TimeStepper value={hh(f.a)} onChange={(v) => { const m = toMin(v); if (m != null) set({ a: Math.min(m, f.b - 15) }); }} label="Příchod" testid="crew-panel-from" changed={!all && f.a !== i0.a} />
              {!all && f.a !== i0.a && <span className="text-xs text-muted-foreground tabular-nums">bylo {hm(i0.a)}</span>}
            </Step>
            <Step prefix="crew-panel" dot="ok" label="Přestávka" editKey="brk" onEdit={toggle("brk")} open={isOpen("brk")}
              value={<span className={dirty && brkChanged ? "text-brand" : ""}>{len} min</span>}
              sub={`${f.auto ? "automaticky" : len ? "zadaná ručně" : "bez přestávky"}${len ? ` · od ${hm(at)} do ${hm(at + len)}` : ""}`}>
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Přestávka">
                <Chip on={f.auto} onClick={() => set({ auto: true })} testid="crew-panel-brk-auto">auto · {autoLen(f.a, f.b)} min</Chip>
                {[0, 15, 30, 45, 60].map((x) => <Chip key={x} on={!f.auto && f.brk === x} onClick={() => set({ auto: false, brk: x })} testid={`crew-panel-brk-${x}`}>{x} min</Chip>)}
              </div>
              {len > 0 && (
                <div className="flex flex-wrap items-center gap-2.5 w-full">
                  <span className="text-[13px] text-muted-foreground">Přestávka od</span>
                  <TimeStepper value={hh(at)} label="Začátek přestávky" testid="crew-panel-brk-at"
                    onChange={(v) => { const m = toMin(v); if (m != null) set({ at: Math.max(f.a, Math.min(f.b - len, m)), auto: false, brk: len }); }} />
                  <span className="text-[13px] text-muted-foreground tabular-nums">do {hm(at + len)}</span>
                </div>
              )}
            </Step>
            <Step prefix="crew-panel" dot="ok" label="Odchod" sub={all ? "stejně všem v partě" : "zapsáno v deníku"} value={tv("b")} editKey="out" onEdit={toggle("out")} open={isOpen("out")} last>
              <TimeStepper value={hh(f.b)} onChange={(v) => { const m = toMin(v); if (m != null) set({ b: Math.max(m, f.a + 15) }); }} label="Odchod" testid="crew-panel-to" changed={!all && f.b !== i0.b} />
              {!all && f.b !== i0.b && <span className="text-xs text-muted-foreground tabular-nums">bylo {hm(i0.b)}</span>}
            </Step>
          </div>
          {all && <p className="text-[12.5px] text-muted-foreground">Kdo byl ten den i na jiné zakázce a čas by se překrýval, zůstane beze změny.</p>}
          {f.err && <p className="text-sm font-semibold text-destructive" data-testid="crew-panel-err">{f.err}</p>}
        </div>

        {(dirty || all) ? (
          <Foot>
            <Button variant="ghost" onClick={all ? onClose : discard} className="h-11 max-md:flex-1" data-testid="crew-panel-discard">{all ? "Zrušit" : "Zahodit"}</Button>
            <Button onClick={save} className="h-11 max-md:flex-1 font-bold px-6" data-testid="crew-panel-save">{all ? "Nastavit všem" : "Uložit"}</Button>
          </Foot>
        ) : <div />}
      </>)}
    </ReviewPanel>
  );
}

// Člověk mimo tým — jen v zápisu deníku a v PDF, do docházky ani nákladů se nepíše
export function ManualPanel({ open, item, onClose, onSave, onRemove }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (!open) return;
    const a = toMin(item?.start), b = toMin(item?.end);
    setF({ name: item?.name || "", role: item?.role || "", kind: item?.kind || "sub", timed: item?.isNew ? true : a != null,
      a: a ?? 8 * 60, b: b ?? 12 * 60, hours: item?.hours ?? "", err: "" });
  }, [open, JSON.stringify(item || {})]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return null;
  const isNew = item?.isNew;
  const save = () => {
    if (!f.name.trim()) return setF({ ...f, err: "Napiš jméno nebo firmu." });
    if (f.timed && f.b - f.a < 15) return setF({ ...f, err: "Konec musí být po začátku." });
    onSave({ name: f.name.trim(), role: f.role.trim(), kind: f.kind, manual: true,
      start: f.timed ? hh(f.a) : null, end: f.timed ? hh(f.b) : null,
      hours: f.timed ? Math.round(((f.b - f.a) / 60) * 100) / 100 : (num(f.hours) || 0) });
  };
  const ph = f.kind === "kd" ? "např. investor, projektant, TDI" : f.kind === "bozp" ? "např. koordinátor BOZP, inspektor práce" : f.kind === "tdi" ? "např. TDI investora" : "např. elektrikář, revize rozvaděče";
  const kindLabel = (KINDS.find(([k]) => k === f.kind) || [])[1];
  const hours = f.timed ? Math.max(0, (f.b - f.a) / 60) : num(f.hours) || 0;
  const field = "h-11 w-full min-w-0 rounded-[12px] border border-border bg-card px-3 text-[16px] md:text-[15px] font-medium text-foreground";
  return (
    <ReviewPanel open={open} onClose={onClose} testid="manual-panel" glass={false} noun="člověk" mobileClass="max-w-xl [&>*]:min-w-0"
      title={`${isNew ? "Přidat" : "Upravit"} člověka mimo tým`}>
      {(controls) => (<>
        <div className="pl-4 sm:pl-6 pr-14 md:pr-5 pt-1 md:pt-4 pb-2 flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <span className="inline-flex items-center gap-1.5 text-[12px] font-bold rounded-full px-2.5 py-1 text-muted-foreground bg-secondary" data-testid="manual-panel-status">
              <UserPlus size={13} /> Mimo tým · jen v deníku</span>
          </div>
          {!isNew && <PanelMenu label="Odstranit z deníku" onRemove={onRemove} testid="manual-panel" />}
          {controls}
        </div>

        <div className="overflow-y-auto overscroll-contain px-4 sm:px-6 pb-6 space-y-3.5 min-w-0">
          <div className="grid justify-items-center text-center gap-1 pt-1">
            <Avatar name={f.name || "?"} size={64} className="bg-secondary text-muted-foreground" />
            <p className="mt-1.5 text-[15px] font-bold max-w-full truncate">{f.name || (isNew ? "Nový člověk" : "—")}{kindLabel ? <span className="text-muted-foreground font-semibold"> · {kindLabel}</span> : null}</p>
            <p className="text-[44px] sm:text-[52px] leading-none font-black tracking-tight tabular-nums mt-1">{fmtHours(Math.round(hours * 100) / 100)}</p>
            <p className="text-[13.5px] text-muted-foreground tabular-nums">{f.timed ? `${hm(f.a)}–${hm(f.b)}` : "bez času"}</p>
          </div>

          <div className="rounded-[20px] bg-background dark:bg-secondary p-3.5 space-y-3">
            <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted-foreground">Jméno nebo firma
              <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, err: "" })} placeholder="např. Elektro Novák, Ing. Malá" autoComplete="off" className={field} data-testid="manual-panel-name" /></label>
            <div>
              <div className="text-[12.5px] font-semibold text-muted-foreground mb-1.5">Kdo to je</div>
              <div className="flex flex-wrap gap-1.5">{KINDS.map(([k, l]) => <Chip key={k} on={f.kind === k} onClick={() => setF({ ...f, kind: k })} testid={`manual-panel-kind-${k}`}>{l}</Chip>)}</div>
            </div>
            <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted-foreground">Profese / poznámka
              <input value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder={ph} autoComplete="off" className={field} data-testid="manual-panel-role" /></label>
          </div>

          <div className={CARD} data-testid="manual-panel-time">
            <div className="flex flex-wrap gap-1.5 py-3">
              <Chip on={f.timed} onClick={() => setF({ ...f, timed: true })} testid="manual-panel-timed">Od – do</Chip>
              <Chip on={!f.timed} onClick={() => setF({ ...f, timed: false })} testid="manual-panel-untimed">Bez času</Chip>
            </div>
            {f.timed && (<>
              <Step prefix="manual-panel" dot="ok" label="Příchod" sub="na stavbě od" value={hm(f.a)} editKey="in" open>
                <TimeStepper value={hh(f.a)} onChange={(v) => { const m = toMin(v); if (m != null) setF({ ...f, a: m, err: "" }); }} label="Příchod" testid="manual-panel-from" />
              </Step>
              <Step prefix="manual-panel" dot="ok" label="Odchod" sub="na stavbě do" value={hm(f.b)} editKey="out" open last>
                <TimeStepper value={hh(f.b)} onChange={(v) => { const m = toMin(v); if (m != null) setF({ ...f, b: m, err: "" }); }} label="Odchod" testid="manual-panel-to" />
              </Step>
            </>)}
          </div>
          <p className="text-[12.5px] text-muted-foreground">Do docházky ani do nákladů zakázky se nezapíše. Je jen v zápisu deníku a v PDF.</p>
          {f.err && <p className="text-sm font-semibold text-destructive">{f.err}</p>}
        </div>

        <Foot>
          <Button variant="ghost" onClick={onClose} className="h-11 max-md:flex-1">Zrušit</Button>
          <Button onClick={save} className="h-11 max-md:flex-1 font-bold px-6" data-testid="manual-panel-save">{isNew ? "Přidat do deníku" : "Uložit"}</Button>
        </Foot>
      </>)}
    </ReviewPanel>
  );
}
